import { withAuth } from './auth';
import type { FileCoordinates } from './coordinates';

export class GitLabApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

export class StaleFileError extends GitLabApiError {}

export interface LoadedFile {
  content: string;
  /** Optimistic lock: passed back on write so a concurrent change is rejected. */
  lastCommitId: string;
}

export interface SaveResult {
  branch: string;
  mergeRequestUrl: string;
}

interface FileResponse {
  content: string;
  encoding: string;
  last_commit_id: string;
}

interface MergeRequestResponse {
  web_url: string;
}

function getGitLabBaseUrl(): string {
  const host = (window as unknown as { env?: Record<string, string> }).env?.REACT_APP_GITLAB_HOST;

  if (!host || host === 'CHANGEME') {
    throw new GitLabApiError('GitLab host is not configured', 0);
  }

  return `https://${host}`;
}

function projectApiUrl(project: string): string {
  return `${getGitLabBaseUrl()}/api/v4/projects/${encodeURIComponent(project)}`;
}

// atob yields one char per byte, so non-ASCII has to be decoded as UTF-8 explicitly.
function decodeBase64Utf8(value: string): string {
  const binary = atob(value.replace(/\s/g, ''));
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

async function readError(response: Response): Promise<string> {
  try {
    const body = await response.json() as { message?: unknown; error?: unknown };
    const detail = body.message ?? body.error;
    return typeof detail === 'string' ? detail : JSON.stringify(detail);
  } catch {
    return response.statusText;
  }
}

export async function loadOntologyFile(coordinates: FileCoordinates): Promise<LoadedFile> {
  const url = `${projectApiUrl(coordinates.project)}/repository/files/${encodeURIComponent(coordinates.path)}`
    + `?ref=${encodeURIComponent(coordinates.ref)}`;

  // The JSON endpoint rather than /raw: the commit id travels in the body, whereas
  // /raw puts it in a header GitLab does not expose to cross-origin readers.
  const response = await withAuth((token) => fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  }));

  if (!response.ok) {
    throw new GitLabApiError(
      `Could not read ${coordinates.path}: ${await readError(response)}`,
      response.status,
    );
  }

  const file = await response.json() as FileResponse;

  return {
    content: file.encoding === 'base64' ? decodeBase64Utf8(file.content) : file.content,
    lastCommitId: file.last_commit_id,
  };
}

function branchName(): string {
  return `ontology-edit/${new Date().toISOString().replace(/[:.]/g, '-')}`;
}

async function commitToBranch(
  coordinates: FileCoordinates,
  content: string,
  lastCommitId: string,
  branch: string,
): Promise<void> {
  const response = await withAuth((token) => fetch(
    `${projectApiUrl(coordinates.project)}/repository/files/${encodeURIComponent(coordinates.path)}`,
    {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        branch,
        start_branch: coordinates.ref,
        content,
        commit_message: 'Update ontology via Witboost editor',
        last_commit_id: lastCommitId,
      }),
    },
  ));

  if (response.ok) {
    return;
  }

  const detail = await readError(response);

  // GitLab reports a lost optimistic lock as a plain 400, so the message is the
  // only thing distinguishing it from a malformed request.
  if (response.status === 400 && /commit|changed|conflict/i.test(detail)) {
    throw new StaleFileError(
      'The file changed in GitLab since it was loaded. Reload to get the latest version — your edits are kept.',
      response.status,
    );
  }

  throw new GitLabApiError(`Commit failed: ${detail}`, response.status);
}

async function openMergeRequest(
  coordinates: FileCoordinates,
  branch: string,
  title: string,
): Promise<string> {
  const response = await withAuth((token) => fetch(
    `${projectApiUrl(coordinates.project)}/merge_requests`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source_branch: branch,
        target_branch: coordinates.ref,
        title,
        description: `Edited with the Witboost ontology editor.\n\nFile: \`${coordinates.path}\``,
        remove_source_branch: true,
      }),
    },
  ));

  if (!response.ok) {
    throw new GitLabApiError(`Commit landed on ${branch} but the merge request failed: ${await readError(response)}`, response.status);
  }

  return (await response.json() as MergeRequestResponse).web_url;
}

/**
 * Commits to a fresh branch and opens a merge request. Ontologies in a governed
 * platform go through review, so nothing is ever written to the target branch.
 */
export async function saveOntologyFile(
  coordinates: FileCoordinates,
  content: string,
  lastCommitId: string,
  ontologyName: string,
): Promise<SaveResult> {
  const branch = branchName();

  await commitToBranch(coordinates, content, lastCommitId, branch);
  const mergeRequestUrl = await openMergeRequest(coordinates, branch, `Update ontology: ${ontologyName}`);

  return { branch, mergeRequestUrl };
}
